import { Link } from '@tanstack/react-router';
import { Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CandidateProfile } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { CustomFieldsCard } from '../../../admin/components/CustomFieldsCard';
import { CandidateCvBankCard } from '../../../cv-bank/components/CandidateCvBankCard';
import { ApplicationJoiningDocuments } from '../../../hires/components/ApplicationJoiningDocuments';
import { CandidatePoolsCard } from '../../../pools/components/CandidatePoolsCard';
import { useUnmerge } from '../../api/candidates-api';
import { DoNotRehireButton, KeepControl, MergeWithButton, consentText, retentionText } from './CandidateActions';
import styles from './CandidatePanel.module.css';

/**
 * Details (CandidatePanel.dc.html): consent and data keeping, the CV Bank, do not rehire, merges
 * and duplicates — and, only while their parts are switched on (#13), talent pools, more details,
 * referred by and joining documents.
 */
export function DetailsTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const { candidate } = profile;
  const canEditCandidate = useCanEdit('candidate');
  const isCareerSiteOn = useModule('career_site');
  const joiningApplicationId = profile.applications.find((row) => row.canOpen && (row.stage === 'hired_pending_join' || row.stage === 'joined'))?.id ?? null;

  return (
    <div className={styles.detailsGrid}>
      <article className={styles.dcard}>
        <h3 className={styles.dcardTitle}>Consent and data keeping</h3>
        <p className={styles.dcardText}>{consentText(candidate)}</p>
        <p className={styles.sub}>{retentionText(candidate)}</p>
        {profile.can.keepPermanently && (
          <div>
            <KeepControl candidate={candidate} />
          </div>
        )}
      </article>

      <ModuleGate module="cv_bank">
        <CandidateCvBankCard candidateId={candidate.id} name={candidate.fullName} />
      </ModuleGate>

      <article className={styles.dcard}>
        <h3 className={styles.dcardTitle}>Do not rehire</h3>
        {candidate.doNotRehire ? <DoNotRehireSet profile={profile} /> : <p className={styles.dcardText}>Not marked.</p>}
        <p className={styles.sub}>Only HR can mark this, and only HR sees the reason.</p>
        {profile.can.setDoNotRehire && (
          <div>
            <DoNotRehireButton candidate={candidate} size="sm" setLabel="Mark do not rehire…" />
          </div>
        )}
      </article>

      <article className={styles.dcard}>
        <h3 className={styles.dcardTitle}>Merges and duplicates</h3>
        <Merges profile={profile} />
        {profile.openFlags.map((flag) => (
          <p key={flag.id} className={styles.sub}>
            May be the same person as {flag.otherName} ({flag.confidence}% match).{' '}
            <Link to="/candidates/duplicates/$flagId" params={{ flagId: flag.id }}>
              Review the match
            </Link>
          </p>
        ))}
        {profile.can.merge && !candidate.mergedIntoCandidateId && (
          <div>
            <MergeWithButton candidate={candidate} size="sm" />
          </div>
        )}
      </article>

      <ModuleGate module="pools">
        <CandidatePoolsCard candidateId={candidate.id} name={candidate.fullName} />
      </ModuleGate>
      <CustomFieldsCard module="candidate" entityId={candidate.id} canEdit={canEditCandidate} />
      {isCareerSiteOn && profile.referrals.length > 0 && (
        <article className={styles.dcard}>
          <h3 className={styles.dcardTitle}>Referred by</h3>
          <ul className={styles.plainList}>
            {profile.referrals.map((referral, index) => (
              <li key={`${referral.referredAt}-${index}`}>
                {referral.referrerName ?? '—'}
                {referral.jobTitle && <span className={styles.quiet}> for {referral.jobTitle}</span>}
              </li>
            ))}
          </ul>
        </article>
      )}
      <ModuleGate module="offers">
        <ApplicationJoiningDocuments applicationId={joiningApplicationId} />
      </ModuleGate>
    </div>
  );
}

function DoNotRehireSet({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const { candidate } = profile;
  const isHidden = candidate.restrictedFields?.includes('doNotRehireReason') ?? false;
  return (
    <>
      <p className={styles.dcardText}>
        Marked do not rehire{candidate.doNotRehireSetAt ? ` on ${formatDate(candidate.doNotRehireSetAt)}` : ''}
        {candidate.doNotRehireSetByName ? ` by ${candidate.doNotRehireSetByName}` : ''}.
      </p>
      <p className={styles.dcardText}>
        <strong>Reason:</strong> {isHidden ? <span title="Only HR can see the reason">—</span> : (candidate.doNotRehireReason ?? '—')}
      </p>
    </>
  );
}

function Merges({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const unmerge = useUnmerge(profile.candidate.id);
  const show = useToasts((state) => state.show);
  const [pendingId, setPendingId] = useState<string | null>(null);

  async function undo(mergeId: string, version: number): Promise<void> {
    setPendingId(mergeId);
    try {
      await unmerge.mutateAsync({ mergeId, version });
      show({ tone: 'success', message: 'Merge undone', detail: 'Both records and their applications are back as they were.' });
    } catch (cause) {
      show({ tone: 'danger', message: "The merge wasn't undone", detail: errorMessage(cause) });
    } finally {
      setPendingId(null);
    }
  }

  if (profile.merges.length === 0) return <p className={styles.dcardText}>Nothing merged into this record.</p>;
  return (
    <ul className={styles.plainList}>
      {profile.merges.map((merge) => (
        <li key={merge.id} className={styles.merge}>
          <span>
            <strong>{merge.mergedName}</strong>{' '}
            <span className={styles.quiet}>
              merged in on {formatDate(merge.mergedAt)}
              {merge.mergedByName ? ` by ${merge.mergedByName}` : ''}
            </span>
          </span>
          <MergeUndo canUnmerge={profile.can.unmerge} isPending={pendingId === merge.id} merge={merge} onUndo={() => void undo(merge.id, merge.version)} />
        </li>
      ))}
    </ul>
  );
}

export function MergeUndo({ canUnmerge, merge, isPending, onUndo }: { readonly canUnmerge: boolean; readonly merge: CandidateProfile['merges'][number]; readonly isPending: boolean; readonly onUndo: () => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  if (!merge.isReversible) return <span className={styles.quiet}>Permanent</span>;
  if (!canUnmerge) return <span className={styles.quiet}>HR can undo this until {formatDate(merge.reversibleUntil)}</span>;
  // Undoing a merge has no undo of its own, so it asks once, beside the button (CLAUDE.md §5, §5a rule 8).
  return (
    <>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={Undo2} isPending={isPending} aria-haspopup="dialog" onClick={toggle}>
        Undo merge
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label="Undo this merge">
        <div className={styles.confirm}>
          <p className={styles.confirmTitle}>Undo this merge? This can't be undone.</p>
          <p className={styles.sub}>Both records and their applications go back as they were.</p>
          <div className={styles.confirmActions}>
            <Button variant="ghost" size="sm" onClick={close}>
              Keep merged
            </Button>
            <Button
              variant="dangerSolid"
              size="sm"
              icon={Undo2}
              onClick={() => {
                close();
                onUndo();
              }}
            >
              Undo merge
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
